import { useMemo } from 'react'

import { useSuspenseQuery } from '@tanstack/react-query'

import { RuleID } from '@forto-dev/insights-engine-models'
import { renderer } from '@forto-dev/renderer-phoenix-ui'
import { dataConstructor, renderContextCreator, templates } from '@forto-dev/renderer-templates'
import {
  ComponentCompositionTemplate,
  CustomerAccountNotification,
  NotificationRenderContext,
} from '@forto-dev/renderer-types'

import { getRendererConfig } from '@/app/config'
import { useAuth } from '@/features/auth'
import { feedActivityToCustomerAccountNotificationMapper } from '@/features/feed/api/feedActivityToCustomerAccountNotificationMapper'
import { transportUnitsQueryOptions } from '@/features/transportUnits'

import { FeedItem } from '../domain/feed'

export const useRendererSDKContext = ({
  item,
  customerAccountNotification,
  channel,
  renderType,
}:
  | {
      item: FeedItem
      customerAccountNotification?: undefined
      channel: 'feed'
      renderType: 'activity'
    }
  | {
      item?: undefined
      customerAccountNotification: CustomerAccountNotification
      channel: 'web'
      renderType: 'notification'
    }): {
  rendererContext: NotificationRenderContext | undefined
} => {
  const notificationReference = item
    ? (item.activity.customField?.ruleID as RuleID)
    : customerAccountNotification.ruleID // TODO: improve this type
  const { user } = useAuth()
  const { data: transportUnits } = useSuspenseQuery(transportUnitsQueryOptions)
  const rendererConfig = useMemo(() => getRendererConfig(transportUnits), [transportUnits])
  const baseTemplate = templates().find((t) => t.id === notificationReference)

  const rendererContext = useMemo(() => {
    let context = undefined
    if (baseTemplate) {
      renderer.init(rendererConfig)
      const { data, config } =
        dataConstructor.constructData(
          item ? feedActivityToCustomerAccountNotificationMapper(item) : customerAccountNotification,
          baseTemplate as ComponentCompositionTemplate,
          { id: user?.id ?? 'unknown', userType: 'public', fullName: `${user?.firstName} ${user?.lastName}` },
          channel,
          renderType,
          rendererConfig
        ) ?? {}
      if (config && data) context = renderContextCreator.create(config, data)
    }
    return context
  }, [
    baseTemplate,
    item,
    customerAccountNotification,
    rendererConfig,
    user?.id,
    user?.firstName,
    user?.lastName,
    channel,
    renderType,
  ])
  return {
    rendererContext,
  }
}
